import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const supportApi = `${process.env.NEXT_PUBLIC_APP_API_URL}/api/support/admin`;

const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

/**
 * GET /api/support/admin/list?status=&category=&q=&page=&limit=
 * Paginated ticket list with optional filters.
 */
export const getSupportListThunk = createAsyncThunk(
    "admin-support-list",
    async ({ token, params = {} }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${supportApi}/list`, { params, ...authHeader(token) });
            return response?.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch tickets" });
        }
    }
);

/**
 * GET /api/support/admin/:ticketCode
 * Full ticket detail including internalNote.
 */
export const getSupportDetailsThunk = createAsyncThunk(
    "admin-support-details",
    async ({ token, ticketCode }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${supportApi}/${ticketCode}`, authHeader(token));
            return response?.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch ticket" });
        }
    }
);

/**
 * POST /api/support/admin/:ticketCode/reply
 * Body: { body, status?: "RESOLVED" | "IN_PROGRESS" | ... }
 * Pushes a reply to ticket.replies[] AND auto-emails the user.
 */
export const replySupportThunk = createAsyncThunk(
    "admin-support-reply",
    async ({ token, ticketCode, body, status }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${supportApi}/${ticketCode}/reply`,
                { body, status },
                authHeader(token)
            );
            return response?.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to post reply" });
        }
    }
);

/**
 * PATCH /api/support/admin/:ticketCode/status
 * Body: { status }
 * Status-only update — does NOT send an email.
 */
export const updateSupportStatusThunk = createAsyncThunk(
    "admin-support-status",
    async ({ token, ticketCode, status }, { rejectWithValue }) => {
        try {
            const response = await axios.patch(
                `${supportApi}/${ticketCode}/status`,
                { status },
                authHeader(token)
            );
            return response?.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to update status" });
        }
    }
);

/**
 * PATCH /api/support/admin/:ticketCode/note
 * Body: { internalNote }
 * Private admin note — not user-visible, never emailed.
 */
export const updateSupportNoteThunk = createAsyncThunk(
    "admin-support-note",
    async ({ token, ticketCode, internalNote }, { rejectWithValue }) => {
        try {
            const response = await axios.patch(
                `${supportApi}/${ticketCode}/note`,
                { internalNote },
                authHeader(token)
            );
            return response?.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to update note" });
        }
    }
);
